/* global __DEV__ */

import getConfig from 'next/config';

import Fetch from 'fetch-ponyfill';
import { eraseCookie, readCookie } from './cookies-browser';

import bog from './moreBog';
import { getLoginUrl } from './urlUtils';
import { WHITE_LIST } from '../config/api/whitelist';
import { isServer } from './isServer';

const { fetch } = Fetch();

const { apiHostname, matchBoxHostName } = getConfig().serverRuntimeConfig;
const { siteApiHost } = getConfig().publicRuntimeConfig;
const NOT_LOGGED_IN = 'Not logged in';
const MATCHBOX_PATH = '/api';
const includeDynamicHostUrl = ['/api'];
const excludeDynamicHostUrl = [
  '/gateway-api/v1/public/authcenter/login',
  '/gateway-api/v1/public/authcenter/auth',
  '/gateway-api/v1/public/common/security/gt-code',
];

export const DEFAULT_TIMEOUT = __DEV__ ? 20000 : 2000;
const SLOW_API_THRESHOLD_MS = 350;

const getLang = lang => lang || (readCookie('lang') || 'en').toLowerCase();

export class NotLoggedInError extends Error {}

export const getUrl = (req, apiPath, isLocal) => {
  if (apiPath.startsWith('http')) return apiPath;
  const isProxyUrl = WHITE_LIST.find(item => apiPath.startsWith(`/${item}`));

  if (isServer) {
    // local
    if (isLocal) {
      const LOCAL_PORT = parseInt(process.env.PORT, 10) || 3000;
      return `${req.protocol}://localhost:${LOCAL_PORT}${apiPath}`;
    }

    // /en api
    if (!isProxyUrl) {
      return `${req.protocol}://${req.get('host')}${apiPath}`;
    }

    // matchbox api
    if (apiPath.startsWith(MATCHBOX_PATH)) {
      return `${matchBoxHostName}${apiPath}`;
    }
    return `${apiHostname}${apiPath}`;
  }

  // browser api: dynamic hostname
  let url = apiPath;
  if (!__DEV__ && includeDynamicHostUrl.find(item => apiPath.startsWith(item)) && !excludeDynamicHostUrl.find(item => apiPath.startsWith(item))) {
    url = `${siteApiHost}${apiPath}`;
  } else {
    url = `${window.location.origin}${apiPath}`;
  }
  return url;
};

export const requestApi = async (apiPath, req, optData, optIsNotJson, isLocal) => {
  const url = getUrl(req, apiPath, isLocal);
  const startTime = Date.now();
  try {
    const requestData = {
      ...optData,
      headers: {
        ...(optData ? optData.headers : {}),
      },
      timeout: DEFAULT_TIMEOUT,
    };
    const res = await fetch(url, requestData);
    const endTime = Date.now();
    const duration = endTime - startTime;
    if ((__DEV__ || req) && duration >= SLOW_API_THRESHOLD_MS) {
      const _start = global.__last_render_start__;
      const _lastRenderText = _start
        ? `Render started ${parseInt(Date.now() - _start, 10)}ms ago`
        : '';
      if (!req) {
        bog.warn('Slow API call!', `${parseInt(duration, 10)}ms`, url, _lastRenderText);
      }
    }

    if (res.status < 200 || res.status >= 300) {
      if (res.status === 401) {
        !req && eraseCookie('cr00');
        !req && eraseCookie('logined');
        throw new NotLoggedInError(NOT_LOGGED_IN);
      }
      if (res.status === 413) {
        throw res;
      }
      let msg = `HTTP status code ${res.status}${__DEV__ || req ? `: ${url}` : ''}`;
      try {
        const data = await res.json();
        const { msg: _msg } = data;
        if (_msg) msg = _msg;
        if (!req) {
          bog.error('Error from server', msg);
        }
      } catch (_err) {
        // noop
      }
      throw new Error(msg);
    }
    const data = (await optIsNotJson) ? res.text() : res.json();
    if (data.error) throw new Error(data.error); // TODO: standard error
    return data;
  } catch (err) {
    if (!req) {
      bog.error(`API Error: ${err.message || err} when calling ${apiPath} with ${url}`);
    }
    throw err;
  }
};

export const requestApiWithToken = async (apiPath, req, optData, optIsNotJson, isLocal, isObj) => {
  optData = optData || {};

  if (optData) {
    if (!optData.headers) {
      optData.headers = {};
    }
    const { method, body } = optData;
    if (/^get$/i.test(method) && body) {
      apiPath += `${apiPath.indexOf('?') === -1 ? '?' : '&'}${body.toString()}`;
      optData.body = null;
    } else if (/^post$/i.test(method)) {
      optData.headers['Content-Type'] = 'application/json';
      optData.body = JSON.stringify(body || {});
    }
  }
  const { headers } = optData;
  const { lang = 'en', csrftoken = '', clienttype = 'web' } = headers;
  optData.headers = {
    ...headers,
    lang,
    clienttype,
    csrftoken,
  };

  const data = await requestApi(apiPath, req, optData, optIsNotJson, isLocal);
  if (!data.success) throw new Error(data.message); // TODO: standard error
  return isObj ? data : data.data;
};

export const requestApiOrLoginRedirect = async (apiPath, req, optData,
  optIsNotJson, isLocal, isNewApi, isObj) => {
  try {
    const res = isNewApi ?
      await requestApiWithToken(apiPath, req, optData, optIsNotJson, isLocal, isObj)
      :
      await requestApi(apiPath, req, optData, optIsNotJson, isLocal);
    return res;
  } catch (err) {
    if (err instanceof NotLoggedInError) {
      window.location = getLoginUrl(getLang(optData.locale), window.location.pathname);
    } else throw err;
  }
  return {};
};

export default requestApi;
